import LockIcon from '@mui/icons-material/Lock'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip
} from '@mui/material'
import { useEffect, useState } from 'react'
import ReactCountryFlag from 'react-country-flag'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { requestWithUserAuth } from '../../../api/auth'
import {
  createRegistration,
  fetchNextTableNumber,
  registerTable,
  updateOwnRegistration,
  updateRegistration
} from '../../../api/snacks'
import { useThemeMode } from '../../../theme'
import { COUNTRIES } from './countries'

const FLAG_STYLE = { width: '1.2em', height: '1.2em', marginRight: '6px', verticalAlign: 'middle' }

const makeStyles = (palette) => ({
  dialogTitle: { color: palette.headerColor },
  formBox: { display: 'flex', flexDirection: 'column', gap: 2, pt: 1 },
  switchLabel: { display: 'flex', alignItems: 'center', gap: 0.5 },
  errorAlert: { mt: 1 },
  spinner: { color: palette.headerColor, mx: 1 },
  submitBtn: { background: palette.headerColor }
})

/**
 * mode: 'user-register' | 'user-edit' | 'admin-create' | 'admin-edit'
 * registration: existing registration object (required for admin-edit and user-edit)
 * onClose: callback to close the form
 */
export const RegistrationForm = ({ mode, registration, onClose }) => {
  const { t } = useTranslation()
  const { palette } = useThemeMode()
  const { config } = useSelector((state) => state.config)
  const auth = useSelector((state) => state.auth)

  const isAdminEdit = mode === 'admin-edit'
  const isAdminCreate = mode === 'admin-create'
  const isUserRegister = mode === 'user-register'
  const isUserEdit = mode === 'user-edit'
  const isUserMode = isUserRegister || isUserEdit

  const [nick, setNick] = useState(
    registration?.owner_nick ?? (isUserMode ? (auth?.profile?.nick ?? '') : '')
  )
  const [badgeNumber, setBadgeNumber] = useState(
    isAdminCreate ? '' : (registration?.user_id ?? '')
  )
  const [description, setDescription] = useState(registration?.description ?? '')
  const [tableNumber, setTableNumber] = useState(
    registration?.table_number != null ? String(registration.table_number) : ''
  )
  const [status, setStatus] = useState(registration?.status ?? 'pending')
  const [country, setCountry] = useState(registration?.country ?? '')

  const [nickError, setNickError] = useState('')
  const [badgeError, setBadgeError] = useState('')
  const [tableError, setTableError] = useState('')
  const [descError, setDescError] = useState('')
  const [conflictError, setConflictError] = useState('')
  const [serverError, setServerError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const styles = makeStyles(palette)

  const NICK_REGEX = /^[\w '\-.]{1,100}$/
  const DESC_MAX = 500

  useEffect(() => {
    if (isAdminCreate) {
      fetchNextTableNumber(config, auth, (n) => {
        setTableNumber((prev) => (prev === '' ? String(n) : prev))
      })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const derivedName = nick.trim() ? `${nick.trim()}'s table` : ''

  const validateNick = (value) => {
    if (!value.trim()) return t('snacks-nick-required')
    if (!NICK_REGEX.test(value.trim())) return t('snacks-nick-invalid')
    return ''
  }

  const validateTableNumber = (value) => {
    if (!value && isAdminCreate) return t('snacks-table-number-required')
    if (value) {
      const n = parseInt(value, 10)
      if (isNaN(n) || n < 1 || !Number.isInteger(n)) return t('snacks-table-number-invalid')
    }
    return ''
  }

  const validateDescription = (value) => {
    if (value.length > DESC_MAX) return t('snacks-description-too-long')
    return ''
  }

  const handleSubmit = () => {
    setConflictError('')
    setServerError('')

    const nickErr = validateNick(nick)
    const badgeErr = isAdminCreate && !badgeNumber.trim() ? t('snacks-badge-required') : ''
    const tableErr = (isAdminCreate || isAdminEdit) ? validateTableNumber(tableNumber) : ''
    const descErr = validateDescription(description)

    setNickError(nickErr)
    setBadgeError(badgeErr)
    setTableError(tableErr)
    setDescError(descErr)

    if (nickErr || badgeErr || tableErr || descErr) return

    setIsLoading(true)

    const DETAIL_KEYS = {
      'User already has an active or pending registration': 'snacks-already-registered',
      'User already has another active registration': 'snacks-error-already-active',
      'table_number already assigned to another registration': 'snacks-table-number-conflict'
    }

    const handleError = (error) => {
      const detail = error?.response?.data?.detail
      const key = detail ? DETAIL_KEYS[detail] : null
      setServerError(key ? t(key) : (detail || t('snacks-error-generic')))
      setIsLoading(false)
    }

    if (isUserRegister) {
      const data = { owner_nick: nick.trim() }
      if (description) data.description = description
      if (country) data.country = country
      requestWithUserAuth(config, auth, (updatedAuth) => {
        registerTable(config, updatedAuth, data,
          () => { setIsLoading(false); if (onClose) onClose() },
          () => { setConflictError(t('snacks-already-registered')); setIsLoading(false) },
          handleError
        )
      })
    } else if (isUserEdit) {
      const data = {}
      if (nick.trim() !== (registration?.owner_nick ?? '')) data.owner_nick = nick.trim()
      if (description !== (registration?.description ?? '')) data.description = description
      if (country !== (registration?.country ?? '')) data.country = country || null
      requestWithUserAuth(config, auth, (updatedAuth) => {
        updateOwnRegistration(config, updatedAuth, data,
          () => { setIsLoading(false); if (onClose) onClose() },
          handleError
        )
      })
    } else if (isAdminCreate) {
      const data = {
        owner_nick: nick.trim(),
        user_id: badgeNumber.trim()
      }
      if (description) data.description = description
      if (tableNumber) data.table_number = parseInt(tableNumber, 10)
      if (country) data.country = country
      requestWithUserAuth(config, auth, (updatedAuth) => {
        createRegistration(config, updatedAuth, data,
          () => { setIsLoading(false); if (onClose) onClose() },
          () => { setConflictError(t('snacks-table-number-conflict')); setIsLoading(false) },
          handleError
        )
      })
    } else if (isAdminEdit) {
      const original = registration ?? {}
      const data = {}
      if (nick.trim() !== (original.owner_nick ?? '')) data.owner_nick = nick.trim()
      if (description !== (original.description ?? '')) data.description = description
      const parsedTable = tableNumber ? parseInt(tableNumber, 10) : null
      if (parsedTable !== (original.table_number ?? null)) data.table_number = parsedTable
      if (status !== original.status) data.status = status
      if (country !== (original.country ?? '')) data.country = country || null
      requestWithUserAuth(config, auth, (updatedAuth) => {
        updateRegistration(config, updatedAuth, original.id, data,
          () => { setIsLoading(false); if (onClose) onClose() },
          handleError
        )
      })
    }
  }

  const isLocked = registration?.is_locked === true

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={styles.dialogTitle}>
        {isUserRegister && t('snacks-register-title')}
        {isUserEdit && t('snacks-edit-my-table')}
        {isAdminCreate && t('snacks-admin-create-title')}
        {isAdminEdit && t('snacks-admin-edit-title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={styles.formBox}>
          <TextField
            label={t('snacks-field-nick')}
            value={nick}
            onChange={(e) => {
              setNick(e.target.value)
              setNickError(e.target.value ? validateNick(e.target.value) : '')
            }}
            error={Boolean(nickError)}
            helperText={nickError || (derivedName ? `→ ${derivedName}` : ' ')}
            required
            fullWidth
            inputProps={{ maxLength: 100 }}
          />

          {isAdminCreate && (
            <TextField
              label={t('snacks-field-badge-number')}
              value={badgeNumber}
              onChange={(e) => {
                setBadgeNumber(e.target.value)
                setBadgeError('')
              }}
              error={Boolean(badgeError)}
              helperText={badgeError || t('snacks-badge-number-hint')}
              required
              fullWidth
              inputProps={{ maxLength: 200 }}
            />
          )}

          {isAdminEdit && (
            <TextField
              label={t('snacks-field-badge-number')}
              value={registration?.user_id ?? ''}
              InputProps={{ readOnly: true }}
              fullWidth
              variant="filled"
            />
          )}

          <TextField
            label={t('snacks-field-description')}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value)
              setDescError(validateDescription(e.target.value))
            }}
            error={Boolean(descError)}
            helperText={descError || `${description.length} / ${DESC_MAX}`}
            multiline
            rows={3}
            fullWidth
            inputProps={{ maxLength: DESC_MAX + 1 }}
          />

          <FormControl fullWidth>
            <InputLabel>{t('snacks-field-country')}</InputLabel>
            <Select
              value={country}
              label={t('snacks-field-country')}
              onChange={(e) => setCountry(e.target.value)}
            >
              <MenuItem value="">{t('snacks-country-none')}</MenuItem>
              {COUNTRIES.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  <ReactCountryFlag countryCode={c.code} svg style={FLAG_STYLE} />
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(isAdminCreate || isAdminEdit) && (
            <TextField
              label={t('snacks-field-table-number')}
              value={tableNumber}
              onChange={(e) => {
                const val = e.target.value
                if (val === '' || /^\d+$/.test(val)) {
                  setTableNumber(val)
                  setTableError(validateTableNumber(val))
                }
              }}
              error={Boolean(tableError)}
              helperText={tableError}
              required={isAdminCreate}
              type="number"
              inputProps={{ min: 1 }}
              fullWidth
            />
          )}

          {isAdminEdit && (
            <Tooltip
              title={isLocked ? t('snacks-locked-tooltip') : ''}
              disableHoverListener={!isLocked}
              arrow
            >
              <span>
                <FormControlLabel
                  control={
                    <Switch
                      checked={status === 'active'}
                      onChange={(e) => setStatus(e.target.checked ? 'active' : 'disabled')}
                      disabled={isLocked}
                    />
                  }
                  label={
                    <Box sx={styles.switchLabel}>
                      {isLocked && <LockIcon fontSize="small" sx={{ opacity: 0.6 }} />}
                      {t('snacks-status-active')}
                    </Box>
                  }
                />
              </span>
            </Tooltip>
          )}

          {(conflictError || serverError) && (
            <Alert severity="error" sx={styles.errorAlert}>{conflictError || serverError}</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>{t('cancel')}</Button>
        {isLoading
          ? <CircularProgress size={28} sx={styles.spinner} />
          : (
              <Button onClick={handleSubmit} variant="contained" sx={styles.submitBtn}>
                {t('save')}
              </Button>
            )}
      </DialogActions>
    </Dialog>
  )
}
